import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { FormModal, Field, TextArea } from "../pages/LeadsPage";
import { LOCATION_TYPES, jobTypeMeta, normalizeItem, installationMeta } from "../lib/jobTypes";
import JobTypeMultiSelect from "./JobTypeMultiSelect";
import JobItemForm from "./JobItemForm";
import ContactPickerField from "./ContactPickerField";
import PeopleMultiSelect from "./PeopleMultiSelect";
import SchedulePreviewModal from "./SchedulePreviewModal";
import { toast } from "sonner";
import { MapPin, ArrowRight, CalendarClock } from "lucide-react";

/**
 * Modal that appears when converting a Quote → Job.
 * Collects everything the shop needs before scheduling.
 * Uses the same multi-item structure as the Quotes and Jobs forms so
 * office staff see one consistent layout across the whole pipeline.
 */
export default function ConvertToJobModal({ quote, onClose, onCreated }) {
  const [installers, setInstallers] = useState([]);
  const [materialsList, setMaterialsList] = useState([]);
  const [materialInput, setMaterialInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);
  const [form, setForm] = useState({
    job_type: quote.job_type || "car",
    vehicle: quote.vehicle || "",
    service: quote.service || "",
    location_type: quote.location_type || "on_site",
    location_address: quote.location_address || "",
    // Customer details are inherited from the quote — editable here in case
    // the receptionist wants to correct/enrich before scheduling.
    contact_name: quote.contact_name || "",
    contact_email: quote.contact_email || "",
    contact_phone: quote.contact_phone || "",
    contact_address: quote.contact_address || "",
    contact_id: quote.contact_id || "",
    assigned_to: "",
    assigned_to_name: "",
    assigned_installer_ids: [],
    assigned_installer_names: [],
    assigned_assistant_ids: [],
    assigned_assistant_names: [],
    scheduled_date: (quote.customer_preferred_dates || [])[0] || "",
    scheduled_end_date: "",
    materials: [],
    notes: quote.notes || "",
    // Carry through the quote's multi-item / installations payload verbatim
    // so conversion doesn't wipe the vehicle + installations data (JobBase
    // has default_factory=list which would otherwise erase the array).
    job_items: (quote.job_items || []).map(normalizeItem),
    amount_override: quote.amount_override || 0,
  });

  useEffect(() => {
    api.get("/users").then(({ data }) => setInstallers(data.filter((u) => u.role === "installer" || u.role === "assistant"))).catch(() => {});
    api.get("/materials").then(({ data }) => setMaterialsList(data)).catch(() => {});
  }, []);

  const meta = jobTypeMeta(form.job_type);

  const addMaterial = () => {
    if (!materialInput.trim()) return;
    setForm((f) => ({ ...f, materials: [...f.materials, materialInput.trim()] }));
    setMaterialInput("");
  };

  const hasCustomerEmail = !!(form.contact_email || quote.contact_email);
  const [sendConfirmation, setSendConfirmation] = useState(!!(quote.contact_email));
  const [confirmationNote, setConfirmationNote] = useState("");

  // Auto-flip the checkbox on when an email becomes available (e.g. after
  // picking a contact). Never auto-flips off — user might have deliberately
  // unchecked it. Purely a "did this newly become possible?" nudge.
  useEffect(() => {
    if (form.contact_email && !sendConfirmation) {
      // Only auto-enable when the customer email was previously absent
      if (!quote.contact_email) setSendConfirmation(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.contact_email]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_id && !form.contact_name) {
      toast.error("Please select or add a customer first");
      return;
    }
    setLoading(true);
    try {
      // Recompose legacy fields + primary job_type from the items array so
      // downstream views (table rows, calendar bars) still render nicely.
      const items = form.job_items || [];
      const legacyVehicle = items
        .map((it) => [it.year, it.make, it.model].filter(Boolean).join(" ") || (it.notes || "").split("\n")[0])
        .filter(Boolean)
        .join(" · ");
      const installLabels = Array.from(new Set(
        items.flatMap((it) => (it.installations || [])
          .map((inst) => installationMeta(inst.installation_type)?.label)
          .filter(Boolean))
      ));
      const legacyService = installLabels.length > 0 ? installLabels.join(" + ") : (form.service || "");
      const payload = {
        ...form,
        job_type: items[0]?.job_type || form.job_type || "car",
        vehicle: legacyVehicle || form.vehicle || "",
        service: legacyService,
      };
      if (form.assigned_to) {
        const u = installers.find((x) => x.id === form.assigned_to);
        if (u) payload.assigned_to_name = u.name || u.email;
      }
      // Multi-crew: derive canonical id + name arrays; mirror the first
      // installer into legacy `assigned_to` for older list/detail views.
      const installerPeople = (form.assigned_installer_ids || [])
        .map((id) => installers.find((u) => u.id === id))
        .filter(Boolean);
      const assistantPeople = (form.assigned_assistant_ids || [])
        .map((id) => installers.find((u) => u.id === id))
        .filter(Boolean);
      payload.assigned_installer_ids = installerPeople.map((u) => u.id);
      payload.assigned_installer_names = installerPeople.map((u) => u.name || u.email);
      payload.assigned_assistant_ids = assistantPeople.map((u) => u.id);
      payload.assigned_assistant_names = assistantPeople.map((u) => u.name || u.email);
      if (installerPeople.length > 0) {
        payload.assigned_to = installerPeople[0].id;
        payload.assigned_to_name = installerPeople[0].name || installerPeople[0].email;
      }
      // Unified pipeline: update the doc's job-specific fields, then advance
      // the same doc from main_status="quote" → "job".
      await api.put(`/jobs/${quote.id}`, {
        ...payload,
        amount: Number(quote.amount) || 0,
        main_status: "quote",
        sub_status: quote.sub_status || "accepted",
      });
      await api.post(`/jobs/${quote.id}/advance`, {
        main_status: "job",
        sub_status: form.scheduled_date ? "booked" : "unbooked",
      });

      // Optionally email the customer a booking confirmation with the
      // scheduled date, vehicle and service — user checked the box in the
      // modal footer. Non-fatal: a Resend hiccup still lets the job book.
      let confirmationOutcome = "";
      if (sendConfirmation) {
        const email = (form.contact_email || quote.contact_email || "").trim();
        if (!email) {
          confirmationOutcome = "no-email";
        } else {
          try {
            const { data } = await api.post(`/jobs/${quote.id}/send-booking-confirmation`, {
              recipient_email: email,
              message: confirmationNote.trim() || null,
            });
            confirmationOutcome = data?.email?.sent ? "sent" : "failed";
          } catch {
            confirmationOutcome = "failed";
          }
        }
      }

      const base = form.scheduled_date ? "Job scheduled" : "Job created (unbooked)";
      if (confirmationOutcome === "sent") toast.success(`${base} · confirmation email sent`);
      else if (confirmationOutcome === "no-email") toast.warning(`${base} · no customer email on file`);
      else if (confirmationOutcome === "failed") toast.warning(`${base} · confirmation email couldn't be sent`);
      else toast.success(base);
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal title="Confirm & schedule this job" onClose={onClose} onSubmit={submit}>
      <div className="text-sm text-slate-400 -mt-1 mb-2 flex items-center gap-1.5">
        <span>{quote.contact_name}</span>
        <ArrowRight className="w-3 h-3 text-slate-500" />
        <span className="text-white font-medium">${Number(quote.amount).toLocaleString()}</span>
      </div>

      {quote.customer_accepted_at && (
        <div
          data-testid="convert-customer-accepted-strip"
          className="bg-emerald-500/[0.08] border border-emerald-500/30 rounded-2xl p-3 mb-2 text-sm space-y-1"
        >
          <div className="text-emerald-300 text-[11px] uppercase tracking-widest">
            Customer accepted{quote.customer_signature_name ? ` — signed ${quote.customer_signature_name}` : ""}
          </div>
          {(quote.customer_preferred_dates || []).length > 0 ? (
            <div className="text-slate-200">
              Preferred dates:{" "}
              {(quote.customer_preferred_dates || []).map((d, i) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setForm({ ...form, scheduled_date: d })}
                  data-testid={`convert-preferred-date-${i}`}
                  className={`inline-block mr-1.5 px-2 py-0.5 rounded-full text-xs border transition ${
                    form.scheduled_date === d
                      ? "bg-emerald-500 text-black border-emerald-400"
                      : "bg-white/[0.05] text-slate-200 border-white/10 hover:bg-white/[0.1]"
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          ) : (
            <div className="text-slate-400 italic">No preferred dates supplied.</div>
          )}
          {quote.customer_message && (
            <div className="text-slate-300 text-xs pt-1">
              <span className="text-slate-500">Message:</span> {quote.customer_message}
            </div>
          )}
        </div>
      )}

      {/* Customer — inherited from the quote, changeable via the picker */}
      <ContactPickerField
        value={form.contact_id}
        required
        testId="convert-customer"
        label="Customer"
        onChange={(c) => {
          if (!c) {
            setForm({
              ...form,
              contact_id: "", contact_name: "", contact_email: "", contact_phone: "", contact_address: "",
            });
            return;
          }
          setForm({
            ...form,
            contact_id: c.id,
            contact_name: c.name || "",
            contact_email: c.email || "",
            contact_phone: c.phone || "",
            contact_address: c.address || "",
            vehicle: form.vehicle || c.vehicle || "",
          });
        }}
      />

      {/* Multi-item picker + per-item vehicle/installation forms — mirrors
          the Quotes + Jobs forms so nothing gets lost in translation. */}
      <JobTypeMultiSelect
        items={form.job_items || []}
        onChange={(items) => setForm({ ...form, job_items: items })}
        testId="convert-job-types"
      />

      {(form.job_items || []).length > 0 && (
        <div className="space-y-3">
          {form.job_items.map((it, idx) => (
            <JobItemForm
              key={it.id}
              item={it}
              index={idx}
              onPatch={(patch) => setForm({
                ...form,
                job_items: form.job_items.map((x, i) => i === idx ? { ...x, ...patch } : x),
              })}
              onRemove={() => setForm({
                ...form,
                job_items: form.job_items.filter((_, i) => i !== idx),
              })}
            />
          ))}
        </div>
      )}

      {/* Fallback single-line vehicle/service — only shown when no items
          have been added yet (legacy quotes without job_items array). */}
      {(!form.job_items || form.job_items.length === 0) && (
        <>
          <Field
            label={meta.itemLabel}
            value={form.vehicle}
            onChange={(v) => setForm({ ...form, vehicle: v })}
            testId="convert-vehicle"
          />
          <Field label="Service / install type" value={form.service} onChange={(v) => setForm({ ...form, service: v })} testId="convert-service" />
        </>
      )}

      {/* Location */}
      <div>
        <label className="text-label block mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> Install location</label>
        <div className="grid grid-cols-2 gap-2">
          {LOCATION_TYPES.map((l) => (
            <button
              key={l.value}
              type="button"
              data-testid={`convert-location-${l.value}`}
              onClick={() => setForm({ ...form, location_type: l.value })}
              className={`text-sm rounded-2xl border py-2 transition ${
                form.location_type === l.value
                  ? "bg-white text-black border-white font-semibold"
                  : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {form.location_type === "off_site" && (
        <Field
          label="Off-site address"
          value={form.location_address}
          onChange={(v) => setForm({ ...form, location_address: v })}
          testId="convert-address"
        />
      )}

      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-3">
          <ConvertSchedulePickerButton
            label="Start date"
            value={form.scheduled_date}
            onOpen={() => setSchedulePickerOpen(true)}
            testId="convert-date"
          />
          <ConvertSchedulePickerButton
            label="End date (optional)"
            value={form.scheduled_end_date}
            onOpen={() => setSchedulePickerOpen(true)}
            testId="convert-end-date"
          />
        </div>
        <div className="text-[11px] text-slate-500">
          Click a date to open the shop schedule and pick around other bookings.
        </div>
      </div>

      <PeopleMultiSelect
        label="Assigned installers"
        testId="convert-assigned-installers"
        options={installers.filter((u) => u.role === "installer")}
        value={form.assigned_installer_ids || []}
        onChange={(ids, people) => setForm({
          ...form,
          assigned_installer_ids: ids,
          assigned_installer_names: people.map((p) => p.name || p.email),
          assigned_to: ids[0] || "",
          assigned_to_name: people[0] ? (people[0].name || people[0].email) : "",
        })}
        placeholder="— pick one or more installers —"
        emptyText="No installers on the roster yet"
      />
      <PeopleMultiSelect
        label="Assigned assistants (prep / disassembly / wash-down)"
        testId="convert-assigned-assistants"
        options={installers.filter((u) => u.role === "assistant")}
        value={form.assigned_assistant_ids || []}
        onChange={(ids, people) => setForm({
          ...form,
          assigned_assistant_ids: ids,
          assigned_assistant_names: people.map((p) => p.name || p.email),
        })}
        placeholder="— pick one or more assistants —"
        emptyText="No assistants on the roster yet"
      />

      <div>
        <label className="text-label block mb-1.5">Materials</label>
        {materialsList.length > 0 && (
          <select
            data-testid="convert-material-picker"
            value=""
            onChange={(e) => {
              const chosen = materialsList.find((m) => m.id === e.target.value);
              if (chosen && !form.materials.includes(chosen.name)) {
                setForm({ ...form, materials: [...form.materials, chosen.name] });
              }
            }}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30 mb-2"
          >
            <option value="" className="bg-neutral-900">— pick from inventory —</option>
            {materialsList.map((m) => (
              <option key={m.id} value={m.id} className="bg-neutral-900">
                {m.name} ({m.stock_qty} {m.unit} in stock)
              </option>
            ))}
          </select>
        )}
        <div className="flex gap-2 mb-2">
          <input
            data-testid="convert-material-input"
            value={materialInput}
            onChange={(e) => setMaterialInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMaterial())}
            placeholder="Or type a custom material"
            className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
          />
          <button type="button" onClick={addMaterial} className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white hover:bg-white/10">Add</button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {form.materials.map((m, i) => (
            <span key={i} className="pill pill-muted text-[10px] gap-2">
              {m}
              <button type="button" onClick={() => setForm({ ...form, materials: form.materials.filter((_, idx) => idx !== i) })} className="text-slate-500 hover:text-white">×</button>
            </span>
          ))}
        </div>
      </div>

      <TextArea label="Notes / brief for installer" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="convert-notes" />

      {/* Booking confirmation email — customer-facing, opt-in per booking */}
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-4 space-y-3">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={sendConfirmation}
            onChange={(e) => setSendConfirmation(e.target.checked)}
            data-testid="convert-send-confirmation"
            className="mt-1 w-4 h-4 accent-emerald-500 cursor-pointer"
          />
          <span className="flex-1">
            <span className="text-white text-sm font-medium block">
              Email booking confirmation to the customer
            </span>
            <span className="text-slate-400 text-xs block mt-0.5">
              {hasCustomerEmail
                ? <>Sends to <span className="text-slate-200">{form.contact_email || quote.contact_email}</span> with the vehicle, service{form.scheduled_date ? ", scheduled date" : ""} and your shop's branding.</>
                : <span className="text-amber-300">No email on file — add one to the customer above to enable.</span>}
            </span>
          </span>
        </label>
        {sendConfirmation && hasCustomerEmail && (
          <textarea
            value={confirmationNote}
            onChange={(e) => setConfirmationNote(e.target.value)}
            rows={2}
            data-testid="convert-confirmation-note"
            placeholder="Optional personal note (appears at the top of the email)"
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30 resize-none"
          />
        )}
      </div>
      {schedulePickerOpen && (
        <SchedulePreviewModal
          initialStart={form.scheduled_date}
          initialEnd={form.scheduled_end_date}
          onClose={() => setSchedulePickerOpen(false)}
          onConfirm={({ start, end }) => {
            setForm({ ...form, scheduled_date: start, scheduled_end_date: end });
            setSchedulePickerOpen(false);
          }}
        />
      )}
    </FormModal>
  );
}


/**
 * Looks like a date field but opens the full month-grid schedule picker
 * (SchedulePreviewModal) instead of the native browser date picker so
 * the office can see every other booking before committing to a slot.
 */
function ConvertSchedulePickerButton({ label, value, placeholder = "dd/mm/yyyy", onOpen, testId }) {
  const display = (() => {
    if (!value) return "";
    const [y, m, d] = String(value).slice(0, 10).split("-");
    if (!y || !m || !d) return value;
    return `${d}/${m}/${y}`;
  })();
  return (
    <div className="space-y-1">
      <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
      <button
        type="button"
        onClick={onOpen}
        data-testid={testId}
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-full bg-white/[0.03] border border-white/10 hover:border-white/25 hover:bg-white/[0.05] text-left transition"
      >
        <span className={display ? "text-white text-sm" : "text-slate-500 text-sm"}>
          {display || placeholder}
        </span>
        <CalendarClock className="w-4 h-4 text-slate-400 shrink-0" />
      </button>
    </div>
  );
}
